import { Card, Col, Container, Form, Row, Alert, Button as BsButton } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { Input } from "./Controls/Input";
import { useState } from "react";
import { useMatch } from "react-router-dom";
import ImageUploader from "./Controls/ImageUploader";
import { FacilityHourlyDetails } from "./Controls/FacilityHourlyDetails";
import { HeaderText } from "./Controls/HeaderText";
import { ResultHourlyEntryPreview } from "./Controls/ResultHourlyEntryPreview";
import { FacilityCreateReq } from "./Data/ApiTypes";
import { Api } from "./Data/Api";
import { Button } from "./Controls/Button";
import { useNavigate } from "react-router-dom";
import { PageBar } from "./Controls/PageBar";
import { Select } from "./Controls/Select";
import { timezones } from "./Data/Timezones";
import { LocationInput } from "./Controls/LocationInput";
import { fileToBase64 } from "./helpers/FileUtils";
import SetupAccountModal from "./SetupAccountModal";
import { RoleList } from "./helpers/RoleManager";
import { authAtom } from "./Data/Atoms";
import { getDefaultStore } from "jotai";

type FacilityCreateInputs = {
    name:              string;
    streetAddress:     string;
    city:              string;
    state:             string;
    zipCode:           string;
    entryInstructions: string;
    description:       string;
    postedCapacity:    number;
    enforcedCapacity:  number;
    clearanceInches:   number;
    website:           string;
    emailAddress:      string;
    phoneNumber:       string;
    latitude:          number;
    longitude:         number;
    timezone:          string;
};

export function FacilityEditPage() {
    const createMode = useMatch( "/operator/facility/create" ) !== null;
    const topLabel   = createMode ? "Create Facility" : "Edit Facility";
    const defaultStore = getDefaultStore();
    const auth = defaultStore.get( authAtom );
    const [thumbnailFile, setThumbnailFile] = useState<File>();
    const [thumbnailPrev, setThumbnailPrev] = useState<string>();
    const [imageMissingMsg, setImageMissingMsg] = useState<string>();

    const [bannerFile, setBannerFile  ] = useState<File>  ();
    const [bannerPrev, setBannerPrev  ] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);

    const [showSetupAccount, setShowSetupAccount] = useState(false);
    const [newFacilityId, setNewFacilityId] = useState<number | null>(null);

    const nav   = useNavigate();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setValue,
        control,
        getValues
    } = useForm<FacilityCreateInputs>( { defaultValues: {
        name:              "",
        streetAddress:     "",
        city:              "",
        state:             "",
        zipCode:           "",
        entryInstructions: "",
        description:       "",
        enforcedCapacity:  0,
        postedCapacity:    0,
        clearanceInches:   77,
        timezone:          "",
    } } );

    function handlePlaceSelect(place: google.maps.places.Place | null) {
    if (place?.addressComponents?.length) {
        const streetNumber = place.addressComponents.find(component =>
        component.types.includes('street_number')
        );
        const route = place.addressComponents.find(component =>
        component.types.includes('route')
        );

        let addressLine1 = '';
        if (streetNumber) {
        addressLine1 += streetNumber.shortText;
        }
        if (route) {
        addressLine1 += (addressLine1 ? ' ' : '') + route.longText;
        }

        if (!addressLine1 && place.addressComponents.length > 0) {
        addressLine1 = place.addressComponents[0].shortText!;
        }

        if (addressLine1) {
        setValue("streetAddress", addressLine1);
        }

        const cityComponent = place.addressComponents.find(component =>
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_3')
        );
        if (cityComponent) {
        setValue("city", cityComponent.longText!);
        }

        const stateComponent = place.addressComponents.find(component =>
        component.types.includes('administrative_area_level_1')
        );
        if (stateComponent) {
        setValue("state", stateComponent.shortText!);
        }

        const zipComponent = place.addressComponents.find(component =>
        component.types.includes('postal_code')
        );
        if (zipComponent) {
        setValue("zipCode", zipComponent.shortText!);
        }
    } else if (place?.formattedAddress) {
        setValue("streetAddress", place.formattedAddress);
    }

    if (place?.location) {
        setValue("latitude", parseFloat(place.location.lat().toFixed(6)));
        setValue("longitude", parseFloat(place.location.lng().toFixed(6)));
    }
    }

    const onSubmit: SubmitHandler<FacilityCreateInputs> = async ( data ) => {
        //validate images are present
        if( !bannerFile || !thumbnailFile ){
            setImageMissingMsg('Please add thumbnail and banner images before saving.')
            return;
        }
        const facilityCreateReq: FacilityCreateReq = {
            name: data.name,
            address: {
                addressLine1: data.streetAddress,
                addressLine2: "",
                city: data.city,
                state: data.state,
                zipCode: data.zipCode,
            },
            instructions: data.entryInstructions,
            postedCapacity: data.postedCapacity,
            enforcedCapacity: data.enforcedCapacity,
            clearanceInches: data.clearanceInches,
            website: data.website,
            emailAddress: data.emailAddress,
            phoneNumber: data.phoneNumber,
            imageThumb: thumbnailFile ? await fileToBase64( thumbnailFile ) : '',
            bannerImage: bannerFile ? await fileToBase64( bannerFile ) : '',
            description: data.description,
            latitude: data.latitude,
            longitude: data.longitude,
            timezone: data.timezone,
        };
        setIsLoading( true );
        Api.facilityCreate( facilityCreateReq ).then( res => {
            if( res.isOk ) {
                setNewFacilityId(Number(res.value));
                
                if (auth.account?.roles.includes(RoleList.SuperAdmin.name)) {
                    setShowSetupAccount(true);                 
                }             
                return;             
            }
            else{
                console.log(res.error);
            }
        } ).finally(() => {
            setIsLoading( false );
        } );

    };

    const { name, entryInstructions } = watch();

    return (
        <Container fluid className="pt-2">
            <Row >
                <Col lg={12} xl={6} xxl={5}>
                
                    <PageBar title={
                        <div className="d-flex align-items-center gap-3">
                            <BsButton variant="link" className="p-0" onClick={() => nav(-1)}>← Back</BsButton>
                            <span>{topLabel}</span>
                        </div>
                    } />
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Input
                            type="text"
                            label="Facility Name"
                            placeholder="Facility Name"
                            error={errors.name}
                            {...register( "name", { required: "Facility Name is required" } )}
                        />
                        <Input
                            type="text"
                            label="Description"
                            placeholder=""
                            error={errors.description}
                            {...register( "description", {
                                required: "Description is required",
                            } )} />

                        <Input
                            type="url"
                            label="Website"
                            placeholder="http://example.com"
                            error={errors.website}
                            {...register( "website", { required: "Website is required" } )}
                        />

                        <div className="two-col">
                            <Input
                                type="email"
                                label="Email"
                                placeholder="admin@garage.com"
                                error={errors.emailAddress}
                                {...register( "emailAddress", { required: "Email is required" } )}
                            />
                            <Input
                                type="tel"
                                label="Phone"
                                placeholder="555-555-5555"
                                error={errors.phoneNumber}
                                {...register("phoneNumber", {
                                    required: "Phone is required",
                                    pattern: {
                                        value:   /^\d{3}-\d{3}-\d{4}$/,
                                        message: "Phone number must be in format ###-###-####"
                                    }
                                })}
                            />
                        </div>

                        <LocationInput
                        name="location"
                        control={control}
                        rules={{ required: "Street address is required" }}
                        onPlaceSelect={handlePlaceSelect}
                        />               

                        <Input
                            type="text"
                            label="City"
                            placeholder="City"
                            error={errors.city}
                            {...register( "city", { required: "City is required" } )}
                        />

                        <div className="two-col">
                            <Input
                                type="text"
                                label="Two Letter State Code"
                                placeholder="Two Letter State Code"
                                error={errors.state}
                                maxLength={2}
                                value={watch( "state" )}
                                {...register( "state", {
                                    required: "State is required",
                                    setValueAs: ( val: string ) => val.toUpperCase(),
                                    pattern: {
                                        value: /^[A-Z]{2}$/,
                                        message: "Please enter a valid two-letter state code"
                                    }
                                } )}
                            />
                            <Input
                                type="text"
                                label="Zip Code"
                                placeholder="Zip Code"
                                error={errors.zipCode}
                                maxLength={10}
                                {...register( "zipCode", {
                                    required: "Zip Code is required",
                                    pattern: {
                                        value: /^\d{5}(-\d{4})?$/,
                                        message: "Please enter a valid zip code"
                                    }
                                } )}
                            />
                        </div>

                        <Input
                            as="textarea"
                            type="text"
                            label="Entry Instructions"
                            placeholder=""
                            error={errors.entryInstructions}
                            {...register( "entryInstructions", {
                                required: "Entry instructions are required",
                            } )} />

                        <Select label="Timezone"
                        {...register( "timezone", { required: true } )}
                                error={undefined}>  
                        <option value="">Select timezone</option>
                        {timezones.map(tz => (
                            <option key={tz.value} value={tz.value}>
                            {tz.label}
                            </option>
                            ))}
                        </Select>                        

                        <Input
                            type="number"
                            label="Bar Height (in Inches)"
                            inputClassName="text-end"
                            placeholder=""
                            error={errors.clearanceInches}
                            {...register( "clearanceInches", {
                                required: "Bar Height is required",
                                valueAsNumber: true,
                            } )} />

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }} className="gap-2">
                            <Input type="number"
                                   label="Posted Capacity"
                                   min={0}
                                   {...register( "postedCapacity", { valueAsNumber: true,
                                                                     required:      "Posted Capacity is required" } )}
                                   explanation="Number of physical spots"
                                   error={errors.postedCapacity} />

                            <Input type="number"
                                   label="Enforced Capacity"
                                   min={0}
                                   {...register( "enforcedCapacity", { valueAsNumber: true,
                                                                       required:      "Enforced Capacity is required" } )}
                                   explanation="Number of spots allowed to be sold"
                                   error={errors.enforcedCapacity} />
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }} className="gap-2 mb-2">
                            <ImageUploader 
                                setFile={setThumbnailFile}
                                preview={thumbnailPrev}
                                setPreview={setThumbnailPrev}>
                                Add Facility Thumbnail
                            </ImageUploader>
                            <ImageUploader 
                                setFile={setBannerFile}
                                preview={bannerPrev}
                                setPreview={setBannerPrev}>
                                Add Facility Banner
                            </ImageUploader>
                        </div>
                        {imageMissingMsg && <Alert variant="danger">
                                {imageMissingMsg}
                            </Alert>}

                        <div className="d-flex justify-content-end mb-4 gap-2">
                            <Button className="w-100" onClick={ () => nav(-1) }>
                                Cancel
                            </Button>
                            <Button className="w-100 btn-main" loading={isLoading} type="submit">
                                Save and Create
                            </Button>
                        </div>
                    </Form>
                </Col>
                <Col lg={12} xl={6} xxl={5} className="p-3">
                    <Card>
                        <Card.Header>
                            <HeaderText>Facility Details Preview</HeaderText>
                        </Card.Header>
                        <Card.Body>
                            <FacilityHourlyDetails
                                name={name}
                                image={bannerPrev}
                                entryInstructions={entryInstructions}
                                type={"Garage"} />
                        </Card.Body>
                    </Card>
                    <div className="mt-3">
                        <HeaderText>Search Result Preview</HeaderText>
                        <div className="">
                            <ResultHourlyEntryPreview
                                name={name}
                                description={watch("description")}
                                duration="12h"
                                thumbnailPrev={thumbnailPrev}
                                price="$20"
                                walkTime="10" />
                        </div>
                    </div>
                </Col>
            </Row>

        <SetupAccountModal
        facilityId={newFacilityId!}
        setShow={setShowSetupAccount}
        show={showSetupAccount}
        facilityName={getValues("name")}
        />
        </Container>
    );
}